import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, dateFmt, formatErr } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Check, X, Send, ClipboardList, CircleCheck, CircleDot } from "lucide-react";

export default function PurchaseRequestDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [pr, setPr] = useState(null);
    const [busy, setBusy] = useState(false);
    const [comment, setComment] = useState("");
    const [rejectOpen, setRejectOpen] = useState(false);

    const load = () => api.get(`/purchase-requests/${id}`).then((r) => setPr(r.data)).catch(() => toast.error("Not found"));
    useEffect(() => { load(); }, [id]);

    if (!pr) return <div className="grid place-items-center py-20 text-brand-emerald"><Loader2 className="h-6 w-6 animate-spin" /></div>;

    const isApprover = ["approver", "tenant_admin"].includes(user.role);
    const canSubmit = ["draft", "rejected"].includes(pr.status) && (pr.requester_id === user.id || user.role === "tenant_admin");

    const act = async (fn, msg) => {
        setBusy(true);
        try { await fn(); toast.success(msg); await load(); }
        catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    const submit = () => act(() => api.post(`/purchase-requests/${id}/submit`), "Submitted for approval");
    const approve = () => act(() => api.post(`/purchase-requests/${id}/approve`, { comment }), "Approved");
    const reject = () => act(async () => { await api.post(`/purchase-requests/${id}/reject`, { comment }); setRejectOpen(false); }, "Rejected");

    return (
        <div className="space-y-6 pe-fade-up max-w-4xl" data-testid="pr-detail">
            <button onClick={() => navigate("/app/requests")} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-brand-emerald">
                <ArrowLeft className="h-4 w-4" /> All requests
            </button>

            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <div className="flex items-center gap-3">
                        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-brand-emerald font-mono">{pr.pr_number}</h1>
                        <StatusBadge status={pr.status} testid="pr-detail-status" />
                    </div>
                    <p className="text-muted-foreground text-sm mt-1">{pr.vendor_name} · {pr.department || "—"} · by {pr.requester_name}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                    {canSubmit && (
                        <Button disabled={busy} onClick={submit} className="bg-brand-emerald hover:bg-brand-emeraldhover rounded-lg" data-testid="submit-pr-button">
                            <Send className="h-4 w-4 mr-1" /> Submit for Approval
                        </Button>
                    )}
                    {pr.status === "in_approval" && isApprover && (
                        <>
                            <Button disabled={busy} onClick={approve} className="bg-emerald-600 hover:bg-emerald-700 rounded-lg" data-testid="approve-button">
                                <Check className="h-4 w-4 mr-1" /> Approve
                            </Button>
                            <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
                                <DialogTrigger asChild>
                                    <Button variant="outline" className="rounded-lg border-rose-200 text-rose-700 hover:bg-rose-50" data-testid="reject-button">
                                        <X className="h-4 w-4 mr-1" /> Reject
                                    </Button>
                                </DialogTrigger>
                                <DialogContent>
                                    <DialogHeader><DialogTitle>Reject request</DialogTitle></DialogHeader>
                                    <Textarea placeholder="Reason (optional)" value={comment} onChange={(e) => setComment(e.target.value)} data-testid="reject-comment" />
                                    <DialogFooter>
                                        <Button variant="outline" onClick={() => setRejectOpen(false)}>Cancel</Button>
                                        <Button className="bg-rose-600 hover:bg-rose-700" onClick={reject} data-testid="confirm-reject-button">Confirm reject</Button>
                                    </DialogFooter>
                                </DialogContent>
                            </Dialog>
                        </>
                    )}
                    {pr.po_id && (
                        <Link to="/app/orders"><Button variant="outline" className="rounded-lg" data-testid="view-po-link"><ClipboardList className="h-4 w-4 mr-1" /> View PO</Button></Link>
                    )}
                </div>
            </div>

            {pr.status === "in_approval" && isApprover && (
                <div>
                    <Textarea placeholder="Add an approval comment (optional)" value={comment} onChange={(e) => setComment(e.target.value)} className="bg-white" data-testid="approve-comment" />
                </div>
            )}

            {/* Line items */}
            <div className="bg-white border border-border rounded-xl overflow-hidden">
                <div className="px-5 py-4 border-b border-border font-semibold text-brand-emerald">Line Items</div>
                <div className="overflow-x-auto pe-scroll">
                    <table className="w-full text-sm">
                        <thead><tr className="text-left text-muted-foreground border-b border-border bg-secondary/40">
                            <th className="p-3 font-medium">Description</th>
                            <th className="p-3 font-medium">GL Account</th>
                            <th className="p-3 font-medium text-right">Qty</th>
                            <th className="p-3 font-medium text-right">Unit</th>
                            <th className="p-3 font-medium text-right">Amount</th>
                        </tr></thead>
                        <tbody>
                            {pr.line_items.map((l, i) => (
                                <tr key={i} className="border-b border-border last:border-0">
                                    <td className="p-3">{l.description}</td>
                                    <td className="p-3 font-mono text-xs">{l.gl_account || "—"}</td>
                                    <td className="p-3 text-right">{l.quantity}</td>
                                    <td className="p-3 text-right font-mono">{money(l.unit_price)}</td>
                                    <td className="p-3 text-right font-mono">{money(Number(l.quantity) * Number(l.unit_price))}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="px-5 py-4 border-t border-border flex justify-end gap-6 bg-secondary/40">
                    <span className="text-sm text-muted-foreground">Total</span>
                    <span className="text-xl font-semibold font-mono text-brand-emerald">{money(pr.total)}</span>
                </div>
            </div>

            {/* Approval chain */}
            {pr.approval_chain?.length > 0 && (
                <div className="bg-white border border-border rounded-xl p-5">
                    <h3 className="font-semibold text-brand-emerald mb-4">Approval Chain</h3>
                    <div className="space-y-3">
                        {pr.approval_chain.map((s, i) => (
                            <div key={i} className="flex items-start gap-3" data-testid={`chain-step-${i}`}>
                                {s.status === "approved" ? <CircleCheck className="h-5 w-5 text-emerald-600 mt-0.5" />
                                    : s.status === "rejected" ? <X className="h-5 w-5 text-rose-600 mt-0.5" />
                                    : <CircleDot className="h-5 w-5 text-amber-500 mt-0.5" />}
                                <div className="flex-1">
                                    <div className="text-sm font-medium">Level {s.level} · {s.rule_name}</div>
                                    <div className="text-xs text-muted-foreground">
                                        {s.status === "pending"
                                            ? (s.level === 1
                                                ? `Awaiting approval — ${s.assigned_approver_name || "requester's initial approver"}`
                                                : `Awaiting approval — ${s.assigned_approver_name || "any approver"}`)
                                            : `${s.status} by ${s.approver_name || "—"} · ${dateFmt(s.acted_at)}`}
                                        {s.comment ? ` — "${s.comment}"` : ""}
                                    </div>
                                </div>
                                <StatusBadge status={s.status === "approved" ? "approved" : s.status === "rejected" ? "rejected" : "in_approval"} />
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Audit */}
            {pr.audit?.length > 0 && (
                <div className="bg-white border border-border rounded-xl p-5">
                    <h3 className="font-semibold text-brand-emerald mb-3">Audit Trail</h3>
                    <ul className="space-y-2 text-sm">
                        {pr.audit.map((a, i) => (
                            <li key={i} className="flex justify-between gap-4 border-b border-border/60 last:border-0 pb-2">
                                <span><span className="font-medium">{a.actor}</span> — {a.action}{a.comment ? ` ("${a.comment}")` : ""}</span>
                                <span className="text-muted-foreground whitespace-nowrap">{dateFmt(a.at)}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}
